import { useMutation } from "@tanstack/react-query";
import {
  ObjectDatasetDataDto,
  ObjectDatasetDto,
  ObjectDatasetReq,
} from "api/model/object-dataset/types";
import ObjectDatasetApi from "api/ObjectDatasetApi";
import { errorToaster, successToaster } from "components/pages/measurement-devices/utils/utils";
import { queryClient } from "services/HttpService";
import {
  defaultResponseHandler,
  getRelatedCacheKeys,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export const useGetObjectDatasets = (companyRef: string, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.OBJECT_DATASET],
    () => ObjectDatasetApi.getObjectDatasets(companyRef),
    null,
    options
  );

export const useGetObjectDataset = (
  identifier: string,
  companyRef: string,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [QueryKey.OBJECT_DATASET, identifier],
    () => ObjectDatasetApi.getObjectDataset(identifier, companyRef),
    null,
    options
  );

export const useCreateOrUpdateObjectDataset = () =>
  useMutation({
    mutationFn: async (data: { body: ObjectDatasetReq; companyRef: string }) => {
      const res = await ObjectDatasetApi.createObjectDataset(data.body, data.companyRef);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      let updating = false;
      queryClient.setQueryData([QueryKey.OBJECT_DATASET], (oldQueryData: ObjectDatasetDto[]) => {
        if (!data) return oldQueryData;
        updating = !!oldQueryData?.find(item => item.identifier === data.identifier);
        if (updating) {
          return oldQueryData.map(item => (item.identifier === data.identifier ? data : item));
        }
        return oldQueryData ? [...oldQueryData, data] : [data];
      });

      if (updating) {
        successToaster("Object dataset updated successfully");
      } else {
        successToaster("Object dataset created successfully");
      }
    },
    onError: (error) => {
      errorToaster(error.message);
    },
  });

export const useDeleteObjectDataset = () =>
  useMutation({
    mutationFn: async (data: { identifier: string; companyRef: string }) => {
      const res = await ObjectDatasetApi.deleteObjectDataset(data.identifier, data.companyRef);
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.OBJECT_DATASET], (oldQueryData: ObjectDatasetDto[]) => {
        if (!data) return oldQueryData;
        return oldQueryData && oldQueryData.filter(item => item.identifier !== data.identifier);
      });
      successToaster("Object dataset deleted successfully");
    },
    onError: error => {
      errorToaster(error.message);
    },
  });

export const useGetObjectDatasetDataInRange = (
  identifier: string,
  companyRef: string,
  startDate: string,
  endDate: string,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [QueryKey.OBJECT_DATASET_DATA, identifier, startDate, endDate],
    () => ObjectDatasetApi.getObjectDatasetDataInRange(identifier, companyRef, startDate, endDate),
    null,
    options
  );

export const useDeleteObjectDatasetData = () =>
  useMutation({
    mutationFn: async (data: { identifier: string; timestamp: string; companyRef: string }) => {
      const res = await ObjectDatasetApi.deleteObjectDatasetData(
        data.identifier,
        data.timestamp,
        data.companyRef
      );
      return defaultResponseHandler(res);
    },
    onSuccess: data => {
      const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.OBJECT_DATASET_DATA);
      queryKeys.forEach(key => {
        if (key.includes(data?.identifier)) {
          queryClient.setQueryData(key, (oldQueryData: ObjectDatasetDataDto[]) => {
            if (!data) return oldQueryData;
            return oldQueryData && oldQueryData.filter(item => item.id !== data.id);
          });
        }
      });
      successToaster("Object dataset data deleted successfully");
    },
    onError: error => {
      errorToaster(error.message);
    },
  });
